/**
 * "I ALREADY PROVED THIS" — the reuse panel.
 *
 * ---------------------------------------------------------------------------
 *  THE DIFFERENCE BETWEEN THIS AND ControlLeverage
 * ---------------------------------------------------------------------------
 * They sit on the same screen and they are not the same sentence:
 *
 *   ControlLeverage   "Prove this once and it ALSO satisfies DORA Art. 12."
 *                     Forward-looking. Shown for every mapped control.
 *   EvidenceReuse     "You ALREADY proved this, over in NIS2, with this file."
 *                     Backward-looking. Shown only when the organisation holds
 *                     a SATISFIED position on the control.
 *
 * So this panel is silent most of the time, and that silence is the feature. A
 * panel that appears on every question saying "you could reuse something" when
 * there is nothing to reuse teaches people to scroll past it, and then they
 * scroll past the one time it matters.
 *
 * ---------------------------------------------------------------------------
 *  THE TWO CARDS ARE DIFFERENT CARDS. THIS IS THE WHOLE CORRECTNESS STORY.
 * ---------------------------------------------------------------------------
 * The server returns `kind: 'SATISFIES'` for a FULL mapping and
 * `kind: 'CONTRIBUTES'` for a PARTIAL one, and they are rendered by two
 * SEPARATE components — `SatisfiesCard` and `ContributesCard` — which share no
 * markup. That is deliberate: a single component with `coverage === 'FULL' ?`
 * sprinkled through it is one careless edit away from rendering a partial
 * mapping in the green box with the accept button, which would tell a customer
 * they are compliant when they are not. Two components cannot drift into each
 * other by accident.
 *
 * What differs, concretely:
 *
 *   FULL        green, headline "You have already proved this", the citation,
 *               where it was proved, the evidence list, and an ACCEPT BUTTON.
 *   PARTIAL     amber, headline "...contributes to X but does not satisfy it",
 *               a mandatory "What this does not cover" block carrying the
 *               mapping's own rationale, the sentence "This cannot be accepted
 *               as proof — you still have to answer this question yourself",
 *               and NO accept button, ever.
 *
 * The two lists are never merged and the PARTIAL card is never collapsed by
 * default to tidy the screen.
 *
 * ---------------------------------------------------------------------------
 *  NOTHING IS AUTO-FILLED
 * ---------------------------------------------------------------------------
 * The panel never answers a question on its own. It states the case and offers
 * a button. After acceptance it keeps stating the case — the reference is
 * re-derived from the live mapping on every render, so if the control is later
 * downgraded or its evidence removed, the justification stops being shown
 * instead of lingering as a stale green claim.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ControlCoverage, EvidenceLifecycleState, OrgControlStatus } from '@opencomply/shared';
import { api, qs, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Button, Card, ErrorNotice, Pill, Skeleton } from './ui';
import { LifecycleBadge } from './EvidenceLifecycle';

// ---------------------------------------------------------------------------
//  Strings
//
//  Inline English defaults in an exported constant, the EVIDENCE_STRINGS
//  pattern. Nothing is added to en.json here, so the locale parity guard is not
//  tripped; the keys are reported to the merger to be added to en.json and
//  el.json together. `{{x}}` is i18next interpolation; `_one` / `_other` are
//  its plural suffixes.
// ---------------------------------------------------------------------------

export const EVIDENCE_REUSE_STRINGS = {
  'reuse.title': 'You have already proved this',
  'reuse.subtitle':
    'Work you did for another framework already bears on this question. Nothing here is uploaded again — the same document is referenced where it already lives.',
  'reuse.loadFailed': 'Could not check what you have already proved.',

  // --- FULL -----------------------------------------------------------------
  'reuse.full.badge': 'Already proved',
  'reuse.full.headline':
    '“{{control}}” is satisfied for your organisation, and it fully satisfies {{ref}} — the requirement this question asks about.',
  'reuse.full.accept': 'Use this — already proved',
  'reuse.full.accepting': 'Recording…',
  'reuse.full.accepted':
    'Recorded as compliant on the strength of “{{control}}”. The evidence stays attached to that control and was not copied.',
  'reuse.full.acceptFailed': 'Could not record that. Try again.',
  /**
   * Server refusals, in the user's language.
   *
   * Every one of these is a RACE, not a mistake the user made: the button is
   * only rendered when the server said the suggestion was acceptable, so
   * reaching one of these means somebody changed the control, its evidence or
   * the submission between this page loading and the click. Each message
   * therefore says what changed and what to do, rather than "that failed".
   *
   * They are matched here rather than added to the shared `error.*` namespace
   * because they are meaningless outside this panel.
   */
  'reuse.error.REUSE_COVERAGE_PARTIAL':
    'This control only partly covers the requirement, so it cannot be accepted as proof. Answer the question yourself.',
  'reuse.error.REUSE_NO_EVIDENCE':
    'The evidence behind that control has been removed, so there is nothing left to reuse. Attach it to the control again.',
  'reuse.error.CONTROL_NOT_SATISFIED':
    'That control is no longer marked satisfied for your organisation, so this question cannot rest on it.',
  'reuse.error.CONTROL_MAPPING_NOT_FOUND':
    'That control is no longer mapped to this requirement.',
  'reuse.error.QUESTION_HAS_NO_COMPLIANT_OPTION':
    'This question has no “compliant” option, so there is nothing to record.',
  'reuse.error.SUBMISSION_FINALIZED':
    'This assessment has been finalised since the page loaded, so its answers can no longer change.',
  'reuse.full.answeredAlready':
    'This question is already answered. The reference below is why it can be.',
  'reuse.full.readOnly':
    'This record is sealed, so the answer cannot be changed. The reference is shown for the audit trail.',

  // --- PARTIAL --------------------------------------------------------------
  // Never says "satisfies", never offers a button, always states the shortfall.
  'reuse.partial.badge': 'Contributes — not proof on its own',
  'reuse.partial.headline':
    '“{{control}}” is satisfied for your organisation and contributes to {{ref}}, but it does not fully satisfy it.',
  'reuse.partial.notCoveredLabel': 'What this control does not cover:',
  'reuse.partial.notCoveredMissing':
    'The catalogue records this mapping as partial but gives no reason. Treat it as not covered and check the citation yourself.',
  'reuse.partial.cannotAccept':
    'This cannot be accepted as proof. You still have to answer this question yourself, and close the shortfall above.',
  'reuse.partial.evidenceMayHelp':
    'Its evidence may still be useful when you answer — open the control to read it.',

  // --- Blocked FULL ---------------------------------------------------------
  'reuse.blocked.NO_EVIDENCE':
    'This control is marked satisfied but has no evidence attached, so there is nothing to reuse. Attach the artefact to the control once, and it will count here.',
  'reuse.blocked.NO_COMPLIANT_OPTION':
    'This question has no “compliant” option to record, so the reference is shown for information only.',

  // --- Provenance -----------------------------------------------------------
  'reuse.provenHeading': 'Where you proved it',
  'reuse.provenAnswered': '{{framework}} {{ref}} ({{code}}) — answered “{{answer}}” on {{date}}',
  'reuse.provenUnanswered': '{{framework}} {{ref}} ({{code}}) — mapped, not yet answered',
  'reuse.provenPartialNote': 'partial mapping',
  'reuse.reviewedBy': 'Control last reviewed {{date}} by {{name}}.',
  'reuse.reviewedOn': 'Control last reviewed {{date}}.',
  'reuse.neverReviewed': 'Nobody has recorded when this control was last reviewed.',

  // --- Evidence -------------------------------------------------------------
  'reuse.evidenceHeading': 'The evidence you already have',
  'reuse.evidenceNote':
    'Attached to the control, not to this answer. One upload, every framework it is mapped to.',
  'reuse.evidenceLink': 'Link',
  'reuse.evidenceUnnamed': 'Untitled item',
  'reuse.openControl': 'Open the control to view or download it',

  // --- Counts ---------------------------------------------------------------
  'reuse.evidenceCount_one': '{{count}} item of evidence',
  'reuse.evidenceCount_other': '{{count}} items of evidence',
  'reuse.otherFrameworks_one': 'also mapped to {{count}} other framework',
  'reuse.otherFrameworks_other': 'also mapped to {{count}} other frameworks',

  // --- Summary (the control catalogue screen) -------------------------------
  'reuse.summary.title': 'What you have already got',
  'reuse.summary.subtitle':
    'Leverage actually realised: what your uploaded evidence is doing for you across every framework it is mapped to.',
  'reuse.summary.loadFailed': 'Could not load the reuse summary.',
  'reuse.summary.controlsProven_one': '{{count}} control proved',
  'reuse.summary.controlsProven_other': '{{count}} controls proved',
  'reuse.summary.evidenceItems_one': '{{count}} document',
  'reuse.summary.evidenceItems_other': '{{count}} documents',
  'reuse.summary.fullRequirements_one': '{{count}} requirement fully discharged',
  'reuse.summary.fullRequirements_other': '{{count}} requirements fully discharged',
  'reuse.summary.partialRequirements_one':
    'and contributes to {{count}} more without discharging it',
  'reuse.summary.partialRequirements_other':
    'and contributes to {{count}} more without discharging them',
  'reuse.summary.frameworks_one': 'across {{count}} framework',
  'reuse.summary.frameworks_other': 'across {{count}} frameworks',
  'reuse.summary.reProvingAvoided_one':
    '{{count}} re-proof avoided — that is how many times you would have had to produce the same evidence again, framework by framework.',
  'reuse.summary.reProvingAvoided_other':
    '{{count}} re-proofs avoided — that is how many times you would have had to produce the same evidence again, framework by framework.',
  'reuse.summary.nothingYet':
    'Nothing is being reused yet. Mark a catalogue control satisfied and attach its evidence once, and every framework requirement mapped to it is supported by that one upload.',
  'reuse.summary.noEvidenceCaveat_one':
    '{{count}} control is marked satisfied with no evidence attached. It is not counted above, because a control nobody can produce an artefact for has avoided no work — it has deferred it.',
  'reuse.summary.noEvidenceCaveat_other':
    '{{count}} controls are marked satisfied with no evidence attached. They are not counted above, because a control nobody can produce an artefact for has avoided no work — it has deferred it.',
  'reuse.summary.byFramework': '{{framework}}: {{full}} fully discharged, {{partial}} contributed to',
  'reuse.summary.noPercentage':
    'These are counts of citations, not a percentage of any framework. A percentage would need invented weights, and an invented weight shown as a coverage figure is the false green this catalogue exists to prevent.',

  // --- Gaps -----------------------------------------------------------------
  'reuse.gap.mayBeCovered': 'May already be covered',
  'reuse.gap.fullLine':
    '“{{control}}” is satisfied for your organisation and fully covers {{ref}}. Check it before planning this work.',
  'reuse.gap.partialLine':
    '“{{control}}” is satisfied and contributes to {{ref}}, but does not cover it. Some of this work is still needed.',
  'reuse.gap.evidence_one': '{{count}} item of evidence already attached',
  'reuse.gap.evidence_other': '{{count}} items of evidence already attached',
  'reuse.gap.hedge':
    'This does not close the gap. The gap records what a finalised assessment found; the control was proved separately, and someone has to decide whether it answers this finding.',
} as const;

export type EvidenceReuseKey = keyof typeof EVIDENCE_REUSE_STRINGS;

/** Keys that come in `_one` / `_other` pairs. See ControlLeverage for why. */
const PLURAL_KEYS = [
  'reuse.evidenceCount',
  'reuse.otherFrameworks',
  'reuse.summary.controlsProven',
  'reuse.summary.evidenceItems',
  'reuse.summary.fullRequirements',
  'reuse.summary.partialRequirements',
  'reuse.summary.frameworks',
  'reuse.summary.reProvingAvoided',
  'reuse.summary.noEvidenceCaveat',
  'reuse.gap.evidence',
] as const;
export type EvidenceReusePluralKey = (typeof PLURAL_KEYS)[number];

// ---------------------------------------------------------------------------
//  Transport types (mirror modules/catalogue/reuse.ts)
// ---------------------------------------------------------------------------

type ReuseKind = 'SATISFIES' | 'CONTRIBUTES';

interface ReuseEvidence {
  id: string;
  kind: string;
  originalName: string | null;
  url: string | null;
  createdAt: string;
  validFrom: string | null;
  validUntil: string | null;
  lifecycleState: EvidenceLifecycleState;
  daysUntilExpiry: number | null;
}

interface ReuseProvenance {
  frameworkKey: string;
  frameworkName: string;
  questionId: string;
  questionCode: string;
  requirementCode: string;
  ref: string;
  coverage: ControlCoverage;
  answerLabel: string | null;
  answerCompliance: string | null;
  answeredAt: string | null;
}

interface ReuseSuggestion {
  kind: ReuseKind;
  controlKey: string;
  controlTitle: string;
  controlDomain: string | null;
  organizationControlId: string;
  controlStatus: OrgControlStatus;
  reviewedAt: string | null;
  reviewedBy: string | null;
  coverage: ControlCoverage;
  ref: string;
  notCovered: string | null;
  acceptable: boolean;
  blockedReason: 'COVERAGE_PARTIAL' | 'NO_EVIDENCE' | 'NO_COMPLIANT_OPTION' | null;
  evidence: ReuseEvidence[];
  evidenceCount: number;
  provenAt: ReuseProvenance[];
  otherFrameworkCount: number;
}

interface QuestionReuseResponse {
  questionId: string;
  questionCode: string;
  compliantOptionId: string | null;
  compliantOptionLabel: string | null;
  suggestions: ReuseSuggestion[];
}

// ---------------------------------------------------------------------------
//  Translation helpers
// ---------------------------------------------------------------------------

export function useReuseStrings() {
  const { t, i18n } = useTranslation();
  const tx = useCallback(
    (key: EvidenceReuseKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: EVIDENCE_REUSE_STRINGS[key], ...options }),
    [t],
  );
  const txn = useCallback(
    (key: EvidenceReusePluralKey, count: number, options?: Record<string, unknown>) =>
      t(key, {
        count,
        ...options,
        defaultValue:
          EVIDENCE_REUSE_STRINGS[
            (count === 1 ? `${key}_one` : `${key}_other`) as EvidenceReuseKey
          ],
      }),
    [t],
  );
  const locale = i18n.language;
  const fmt = useCallback(
    (iso: string) => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso)),
    [locale],
  );
  return { tx, txn, fmt };
}

// ===========================================================================
//  THE QUESTION PANEL
// ===========================================================================

export function EvidenceReuse({
  questionId,
  submissionId,
  answeredOptionId,
  readOnly = false,
}: {
  questionId: string;
  /** Needed to record an acceptance. Omit on screens with no submission. */
  submissionId?: string;
  /** The option currently recorded, so an accepted suggestion reads as such. */
  answeredOptionId?: string | null;
  readOnly?: boolean;
}) {
  const { tx } = useReuseStrings();
  const { allows } = useAuth();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'reuse', 'question', questionId],
    queryFn: () =>
      api.get<QuestionReuseResponse>(`/catalogue/reuse/question${qs({ questionId })}`),
  });

  if (isLoading) {
    return (
      <Card className="p-4 space-y-3">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }

  if (isError) {
    return <ErrorNotice message={tx('reuse.loadFailed')} onRetry={() => void refetch()} />;
  }

  const suggestions = data?.suggestions ?? [];
  // Silence when there is nothing already proved. See the header comment.
  if (suggestions.length === 0) return null;

  const canAnswer = !readOnly && allows('submission.answer') && !!submissionId;
  const alreadyAnswered =
    !!data?.compliantOptionId && answeredOptionId === data.compliantOptionId;

  return (
    <section aria-labelledby={`reuse-h-${questionId}`} className="space-y-3">
      <div>
        <h3 id={`reuse-h-${questionId}`} className="text-sm font-semibold text-ink">
          {tx('reuse.title')}
        </h3>
        <p className="text-xs text-ink-muted mt-0.5">{tx('reuse.subtitle')}</p>
      </div>

      <ul className="space-y-3">
        {suggestions.map((s) =>
          /*
            THE FORK. Two components, no shared markup, so a PARTIAL mapping
            cannot inherit the green box or the accept button by accident.
          */
          s.kind === 'SATISFIES' ? (
            <SatisfiesCard
              key={s.controlKey}
              suggestion={s}
              questionId={questionId}
              submissionId={submissionId}
              canAnswer={canAnswer}
              readOnly={readOnly}
              alreadyAnswered={alreadyAnswered}
            />
          ) : (
            <ContributesCard key={s.controlKey} suggestion={s} />
          ),
        )}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Shared sub-blocks
//
//  These are the parts that are genuinely the SAME statement in both cases —
//  where the control was proved, and what is attached to it. The parts that
//  differ (the headline, the colour, the shortfall, the button) are not here,
//  deliberately.
// ---------------------------------------------------------------------------

function Provenance({ items }: { items: ReuseProvenance[] }) {
  const { tx, fmt } = useReuseStrings();
  if (items.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-ink">{tx('reuse.provenHeading')}</p>
      <ul className="space-y-0.5">
        {items.map((p) => (
          <li key={`${p.frameworkKey}-${p.questionCode}`} className="text-xs text-ink-muted">
            {p.answerLabel && p.answeredAt
              ? tx('reuse.provenAnswered', {
                  framework: p.frameworkName,
                  ref: p.ref,
                  code: p.questionCode,
                  answer: p.answerLabel,
                  date: fmt(p.answeredAt),
                })
              : tx('reuse.provenUnanswered', {
                  framework: p.frameworkName,
                  ref: p.ref,
                  code: p.questionCode,
                })}
            {/* A partial mapping elsewhere is labelled there too, so this list
                cannot be read as "all of these are fully discharged". */}
            {p.coverage === 'PARTIAL' && (
              <span className="ml-1 text-warning">({tx('reuse.provenPartialNote')})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewedLine({ suggestion }: { suggestion: ReuseSuggestion }) {
  const { tx, fmt } = useReuseStrings();
  if (!suggestion.reviewedAt) {
    return <p className="text-xs text-ink-muted">{tx('reuse.neverReviewed')}</p>;
  }
  return (
    <p className="text-xs text-ink-muted">
      {suggestion.reviewedBy
        ? tx('reuse.reviewedBy', {
            date: fmt(suggestion.reviewedAt),
            name: suggestion.reviewedBy,
          })
        : tx('reuse.reviewedOn', { date: fmt(suggestion.reviewedAt) })}
    </p>
  );
}

/**
 * The evidence, listed by name and validity, with a link to where it lives.
 *
 * It is a LIST, not a second upload panel, and that is the point: there is
 * exactly one place this organisation's artefact for this control lives, and
 * the link goes there. A panel here that could accept files would be a second
 * attachment point for the same control — which is how one document becomes
 * three and an auditor gets three checksums for one certificate.
 */
function EvidenceList({ suggestion }: { suggestion: ReuseSuggestion }) {
  const { tx, txn } = useReuseStrings();

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-ink">
        {tx('reuse.evidenceHeading')}{' '}
        <span className="font-normal text-ink-muted">
          ({txn('reuse.evidenceCount', suggestion.evidenceCount)})
        </span>
      </p>
      <p className="text-xs text-ink-muted">{tx('reuse.evidenceNote')}</p>
      <ul className="space-y-1">
        {suggestion.evidence.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-ink truncate max-w-[22rem]">
              {e.originalName ?? (e.kind === 'LINK' ? e.url : null) ?? tx('reuse.evidenceUnnamed')}
            </span>
            {e.kind === 'LINK' && <Pill tone="neutral">{tx('reuse.evidenceLink')}</Pill>}
            {/* Expiry is stated beside every item. Reused evidence that has
                lapsed is worse than none: it is silently relied on by several
                frameworks at once. */}
            <LifecycleBadge item={e} />
          </li>
        ))}
      </ul>
      <Link
        to={`/catalogue/${encodeURIComponent(suggestion.controlKey)}`}
        className="text-xs text-brand hover:underline"
      >
        {tx('reuse.openControl')}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  FULL — "you have already proved this"
// ---------------------------------------------------------------------------

function SatisfiesCard({
  suggestion,
  questionId,
  submissionId,
  canAnswer,
  readOnly,
  alreadyAnswered,
}: {
  suggestion: ReuseSuggestion;
  questionId: string;
  submissionId?: string;
  canAnswer: boolean;
  readOnly: boolean;
  alreadyAnswered: boolean;
}) {
  const { tx, txn } = useReuseStrings();
  const describeApiError = useApiError();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () =>
      api.post('/catalogue/reuse/accept', {
        submissionId,
        questionId,
        controlKey: suggestion.controlKey,
      }),
    onSuccess: () => {
      setError(null);
      // The submission carries the answer; the catalogue carries the position.
      void queryClient.invalidateQueries({ queryKey: ['submission'] });
      void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
    },
    onError: (err) => {
      // Our own codes first — `useApiError` would fall back to the generic
      // "something went wrong", which tells a user nothing about a control
      // that was downgraded under them.
      const own =
        err instanceof ApiError &&
        `reuse.error.${err.code}` in EVIDENCE_REUSE_STRINGS
          ? tx(`reuse.error.${err.code}` as EvidenceReuseKey)
          : null;
      setError(own ?? describeApiError(err) ?? tx('reuse.full.acceptFailed'));
    },
  });

  return (
    <li>
      <Card className="p-4 space-y-3 border-good/40 bg-good/[0.04]">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{suggestion.controlTitle}</p>
            {suggestion.controlDomain && (
              <p className="text-xs text-ink-muted mt-0.5">{suggestion.controlDomain}</p>
            )}
          </div>
          <Pill tone="good">{tx('reuse.full.badge')}</Pill>
        </div>

        <p className="text-sm text-ink">
          {tx('reuse.full.headline', {
            control: suggestion.controlTitle,
            ref: suggestion.ref,
          })}
        </p>

        <Provenance items={suggestion.provenAt} />
        <ReviewedLine suggestion={suggestion} />

        {suggestion.evidenceCount > 0 ? (
          <EvidenceList suggestion={suggestion} />
        ) : (
          <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
            {tx('reuse.blocked.NO_EVIDENCE')}
          </p>
        )}

        {suggestion.otherFrameworkCount > 0 && (
          <p className="text-xs text-ink-muted">
            {txn('reuse.otherFrameworks', suggestion.otherFrameworkCount)}
          </p>
        )}

        {/* --- The one action ------------------------------------------------
            Offered only when the server said `acceptable`. The server re-checks
            the same rule, so this is a usability affordance, not the control. */}
        {alreadyAnswered ? (
          <p className="text-xs text-ink-secondary">{tx('reuse.full.answeredAlready')}</p>
        ) : readOnly ? (
          <p className="text-xs text-ink-muted">{tx('reuse.full.readOnly')}</p>
        ) : suggestion.blockedReason === 'NO_COMPLIANT_OPTION' ? (
          <p className="text-xs text-ink-muted">{tx('reuse.blocked.NO_COMPLIANT_OPTION')}</p>
        ) : suggestion.acceptable && canAnswer ? (
          <div className="space-y-1.5">
            <Button
              variant="primary"
              size="sm"
              disabled={accept.isPending}
              onClick={() => accept.mutate()}
            >
              {accept.isPending ? tx('reuse.full.accepting') : tx('reuse.full.accept')}
            </Button>
            {accept.isSuccess && (
              <p className="text-xs text-good">
                {tx('reuse.full.accepted', { control: suggestion.controlTitle })}
              </p>
            )}
            {error && <p className="text-xs text-critical">{error}</p>}
          </div>
        ) : null}
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------
//  PARTIAL — "contributes, and here is what it does not cover"
//
//  Shares no markup with SatisfiesCard. No green. No button. The shortfall is
//  rendered before the evidence, because a reader who stops after two lines
//  must have read the limitation and not the reassurance.
// ---------------------------------------------------------------------------

function ContributesCard({ suggestion }: { suggestion: ReuseSuggestion }) {
  const { tx, txn } = useReuseStrings();

  return (
    <li>
      <Card className="p-4 space-y-3 border-warning/50 bg-warning/[0.06]">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{suggestion.controlTitle}</p>
            {suggestion.controlDomain && (
              <p className="text-xs text-ink-muted mt-0.5">{suggestion.controlDomain}</p>
            )}
          </div>
          <Pill tone="warning">{tx('reuse.partial.badge')}</Pill>
        </div>

        <p className="text-sm text-ink">
          {tx('reuse.partial.headline', {
            control: suggestion.controlTitle,
            ref: suggestion.ref,
          })}
        </p>

        {/* MANDATORY. Rendered whether or not the catalogue supplied a reason:
            an absent rationale is reported as an absent rationale, never as
            nothing to report. */}
        <div className="rounded-md border border-warning/50 bg-surface-raised p-2.5">
          <p className="text-xs font-semibold text-ink">{tx('reuse.partial.notCoveredLabel')}</p>
          <p className="text-xs text-ink-secondary mt-0.5 leading-relaxed">
            {suggestion.notCovered ?? tx('reuse.partial.notCoveredMissing')}
          </p>
        </div>

        <p className="text-xs font-medium text-ink">{tx('reuse.partial.cannotAccept')}</p>

        <Provenance items={suggestion.provenAt} />
        <ReviewedLine suggestion={suggestion} />

        {suggestion.evidenceCount > 0 && (
          <>
            <p className="text-xs text-ink-muted">{tx('reuse.partial.evidenceMayHelp')}</p>
            <EvidenceList suggestion={suggestion} />
          </>
        )}

        {suggestion.otherFrameworkCount > 0 && (
          <p className="text-xs text-ink-muted">
            {txn('reuse.otherFrameworks', suggestion.otherFrameworkCount)}
          </p>
        )}
      </Card>
    </li>
  );
}

// ===========================================================================
//  THE SUMMARY — "what you have already got"
// ===========================================================================

export interface ReuseSummaryResponse {
  controlsSatisfied: number;
  controlsProven: number;
  evidenceItems: number;
  fullRequirements: number;
  partialRequirements: number;
  frameworksReached: number;
  reProvingAvoided: number;
  controlsSatisfiedWithoutEvidence: number;
  byFramework: Array<{ key: string; name: string; full: number; partial: number }>;
}

/**
 * The number that sells the product, stated honestly.
 *
 * FULL and PARTIAL are two sentences and are never added together. There is no
 * percentage and no progress bar — see `reuse.summary.noPercentage`, which is
 * shown to the reader rather than only written in a comment, because the
 * person most likely to misread these counts is the one looking at them.
 */
export function ReuseSummary() {
  const { tx, txn } = useReuseStrings();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'reuse', 'summary'],
    queryFn: () => api.get<ReuseSummaryResponse>('/catalogue/reuse/summary'),
  });

  if (isLoading) return <Skeleton className="h-28 w-full" />;
  if (isError) {
    return <ErrorNotice message={tx('reuse.summary.loadFailed')} onRetry={() => void refetch()} />;
  }
  if (!data) return null;

  const nothingYet = data.controlsProven === 0;

  return (
    <Card className="p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-ink">{tx('reuse.summary.title')}</h2>
        <p className="text-xs text-ink-muted mt-0.5">{tx('reuse.summary.subtitle')}</p>
      </div>

      {nothingYet ? (
        <p className="text-sm text-ink-secondary">{tx('reuse.summary.nothingYet')}</p>
      ) : (
        <>
          <p className="text-sm text-ink leading-relaxed">
            {txn('reuse.summary.evidenceItems', data.evidenceItems)}
            {' · '}
            {txn('reuse.summary.controlsProven', data.controlsProven)}
            {' · '}
            {txn('reuse.summary.fullRequirements', data.fullRequirements)}
            {' '}
            {txn('reuse.summary.frameworks', data.frameworksReached)}
          </p>

          {/* Separate sentence, separate line, never summed with the above. */}
          {data.partialRequirements > 0 && (
            <p className="text-sm text-ink-muted">
              {txn('reuse.summary.partialRequirements', data.partialRequirements)}
            </p>
          )}

          <p className="text-sm font-semibold text-ink">
            {txn('reuse.summary.reProvingAvoided', data.reProvingAvoided)}
          </p>

          <ul className="space-y-0.5">
            {data.byFramework.map((f) => (
              <li key={f.key} className="text-xs text-ink-muted">
                {tx('reuse.summary.byFramework', {
                  framework: f.name,
                  full: f.full,
                  partial: f.partial,
                })}
              </li>
            ))}
          </ul>
        </>
      )}

      {data.controlsSatisfiedWithoutEvidence > 0 && (
        <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
          {txn('reuse.summary.noEvidenceCaveat', data.controlsSatisfiedWithoutEvidence)}
        </p>
      )}

      <p className="text-xs text-ink-muted italic">{tx('reuse.summary.noPercentage')}</p>
    </Card>
  );
}

// ===========================================================================
//  GAPS — "the work may already be done"
// ===========================================================================

export interface GapReuseEntry {
  gapId: string;
  questionCode: string | null;
  controls: Array<{
    kind: ReuseKind;
    controlKey: string;
    controlTitle: string;
    coverage: ControlCoverage;
    ref: string;
    notCovered: string | null;
    reviewedAt: string | null;
    evidenceCount: number;
  }>;
}

/** Fetch once for the whole gap list, then index by gap id. */
export function useGapReuse(enabled: boolean) {
  const { data } = useQuery({
    queryKey: ['catalogue', 'reuse', 'gaps'],
    queryFn: () => api.get<{ items: GapReuseEntry[] }>('/catalogue/reuse/gaps'),
    enabled,
  });
  return new Map((data?.items ?? []).map((i) => [i.gapId, i]));
}

/**
 * The reuse note on one gap.
 *
 * It does NOT close the gap, change its status, or claim it is remediated — a
 * gap is what a finalised assessment found, and a control proved afterwards is
 * a lead, not a correction to a sealed record. The hedge is rendered, not
 * implied.
 */
export function GapReuseNote({ entry }: { entry: GapReuseEntry }) {
  const { tx, txn } = useReuseStrings();
  if (entry.controls.length === 0) return null;

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-sunken p-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="neutral">{tx('reuse.gap.mayBeCovered')}</Pill>
      </div>
      <ul className="space-y-1">
        {entry.controls.map((c) => (
          <li key={c.controlKey} className="text-xs">
            {/* The same fork as the question panel: a FULL line and a PARTIAL
                line that say different things and are never interchanged. */}
            <p className={c.kind === 'SATISFIES' ? 'text-ink' : 'text-ink-muted'}>
              {c.kind === 'SATISFIES'
                ? tx('reuse.gap.fullLine', { control: c.controlTitle, ref: c.ref })
                : tx('reuse.gap.partialLine', { control: c.controlTitle, ref: c.ref })}
            </p>
            {c.kind === 'CONTRIBUTES' && c.notCovered && (
              <p className="text-ink-secondary mt-0.5">
                <span className="font-medium">{tx('reuse.partial.notCoveredLabel')}</span>{' '}
                {c.notCovered}
              </p>
            )}
            {c.evidenceCount > 0 && (
              <p className="text-ink-muted mt-0.5">
                {txn('reuse.gap.evidence', c.evidenceCount)}
                {' · '}
                <Link
                  to={`/catalogue/${encodeURIComponent(c.controlKey)}`}
                  className="text-brand hover:underline"
                >
                  {tx('reuse.openControl')}
                </Link>
              </p>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-ink-muted italic">{tx('reuse.gap.hedge')}</p>
    </div>
  );
}
